import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";
import Logo from "@/components/Logo";
import { isMuted, setMuted, sfx } from "@/lib/sounds";

export function scrollToId(id) {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
  else el.scrollIntoView({ behavior: "smooth" });
}

const LINKS = [
  { id: "vault", label: "The Vault" },
  { id: "gallery", label: "Gallery" },
  { id: "stickers", label: "Stickers" },
];

export default function Navbar() {
  const [muted, setM] = useState(isMuted());
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    setM(next);
    if (!next) sfx.click();
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 2.1, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-ink/85 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-8">
        <button
          data-testid="nav-logo-btn"
          onClick={() => {
            sfx.click();
            if (window.__lenis) window.__lenis.scrollTo(0);
            else window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          aria-label="Back to top"
        >
          <Logo />
        </button>

        <nav className="hidden items-center gap-8 md:flex">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}-link`}
              onClick={() => {
                sfx.click();
                scrollToId(l.id);
              }}
              className="text-sm font-medium text-paper/80 transition-colors hover:text-pumpkin"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            data-testid="nav-mute-btn"
            onClick={toggleMute}
            aria-label={muted ? "Unmute sounds" : "Mute sounds"}
            className="rounded-full border border-white/15 bg-white/5 p-2.5 text-paper/90 transition-all hover:border-slime hover:text-slime"
          >
            {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>
          <button
            data-testid="nav-cta-btn"
            onClick={() => {
              sfx.boop();
              scrollToId("vault");
            }}
            className="rounded-full bg-pumpkin px-5 py-2.5 text-sm font-semibold text-ink shadow-glow-orange transition-transform hover:scale-105 active:scale-95"
          >
            Color Now
          </button>
        </div>
      </div>
    </motion.header>
  );
}
